import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Menu, X, User, LogOut, Calendar } from 'lucide-react';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { LanguageSelector, CurrencySelector } from './LocaleSelectors';

export const CustomerNav = () => {
  const { user, logout } = useAuth();
  const { t, isRTL } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: t('home') },
    { path: '/services', label: t('services') },
    { path: '/booking', label: t('bookNow') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-deep-charcoal/5">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className={`flex items-center space-x-2 ${isRTL ? 'space-x-reverse' : ''}`} data-testid="nav-logo">
            <span className="font-heading text-2xl font-bold text-terracotta tracking-tight">FTASM</span>
          </Link>

          {/* Desktop Navigation */}
          <div className={`hidden md:flex items-center space-x-1 ${isRTL ? 'space-x-reverse' : ''}`}>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`px-4 py-2 rounded-lg font-body text-sm font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-terracotta bg-terracotta/10'
                    : 'text-deep-charcoal hover:text-terracotta hover:bg-terracotta/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Locale Selectors & Auth Buttons */}
          <div className={`hidden md:flex items-center space-x-3 ${isRTL ? 'space-x-reverse' : ''}`}>
            <LanguageSelector />
            <CurrencySelector />
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className={`flex items-center space-x-2 text-deep-charcoal hover:text-terracotta ${isRTL ? 'space-x-reverse' : ''}`}
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-5 h-5" />
                    <span className="font-body">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-white border-deep-charcoal/10">
                  <DropdownMenuItem asChild>
                    <Link to="/my-bookings" className="flex items-center" data-testid="my-bookings-link">
                      <Calendar className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {t('myBookings')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-deep-charcoal hover:text-terracotta" data-testid="login-btn">
                    {t('signIn')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="rounded-full px-6 bg-terracotta text-white hover:bg-terracotta-dark" data-testid="register-btn">
                    {t('getStarted')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg text-deep-charcoal hover:bg-terracotta/10"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-deep-charcoal/10">
            <div className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg font-body text-sm font-medium ${
                    isActive(link.path)
                      ? 'text-terracotta bg-terracotta/10'
                      : 'text-deep-charcoal hover:text-terracotta'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Mobile Locale Selectors */}
              <div className={`flex items-center gap-2 px-4 py-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <LanguageSelector />
                <CurrencySelector />
              </div>
              
              <div className="pt-4 border-t border-deep-charcoal/10">
                {user ? (
                  <>
                    <Link
                      to="/my-bookings"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 text-deep-charcoal font-body"
                    >
                      {t('myBookings')}
                    </Link>
                    <button
                      onClick={() => { logout(); setMobileMenuOpen(false); }}
                      className="block w-full text-left px-4 py-3 text-red-600 font-body"
                    >
                      {t('logout')}
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col space-y-2 px-4">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full">{t('signIn')}</Button>
                    </Link>
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-terracotta text-white">{t('getStarted')}</Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
